import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import ConfirmDialog from './ConfirmDialog';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import './DashboardView.css';

/**
 * Reusable Dashboard Component for displaying project overview
 * Used in both configuration preview and service sections
 */
const DashboardView = ({
  selectedProject,
  selectedResource,
  configuration,
  onResourceSelect,
  onOpenConsole,
  onProjectDeleted
}) => {
  const [environmentsStatus, setEnvironmentsStatus] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(true); // Start as true for initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch environment status for each environment
  const fetchEnvironmentsStatus = async (isBackground = false) => {
    if (!selectedProject?.id || !selectedProject?.environments) {
      return;
    }

    // Only show loading spinner on initial load, not during background refreshes
    if (!isBackground) {
      setLoadingStatus(true);
    }
    const statusMap = {};

    try {
      // Fetch pods status for each environment
      await Promise.all(
        selectedProject.environments.map(async (env) => {
          try {
            const pods = await miaPlatformDataService.getPodsStatus(
              selectedProject.id,
              env.envId || env
            );

            console.log(`Pods data for ${env.envId || env}:`, pods);

            // Handle different response formats
            const podsList = Array.isArray(pods) ? pods : (pods?.items || []);
            
            // Determine environment health based on pods
            const totalPods = podsList?.length || 0;
            const runningPods = podsList?.filter(pod => {
              const phase = pod.status?.phase || pod.phase;
              return phase === 'Running' || phase === 'running';
            }).length || 0;
            const failedPods = podsList?.filter(pod => {
              const phase = pod.status?.phase || pod.phase;
              return phase === 'Failed' || phase === 'failed';
            }).length || 0;

            statusMap[env.envId || env] = {
              status: failedPods > 0 ? 'error' : runningPods > 0 ? 'active' : 'inactive',
              totalPods,
              runningPods,
              failedPods,
              pods: podsList
            };
          } catch (error) {
            console.error(`Failed to fetch status for ${env.envId || env}:`, error);
            statusMap[env.envId || env] = {
              status: 'unknown',
              error: error.message
            };
          }
        })
      );

      setEnvironmentsStatus(statusMap);
    } catch (error) {
      console.error('Error fetching environments status:', error);
    } finally {
      setLoadingStatus(false);
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  };

  // Fetch on mount and set up polling every 30 seconds
  useEffect(() => {
    fetchEnvironmentsStatus(false); // Initial load with loading indicator

    // Set up polling interval
    const pollInterval = setInterval(() => {
      fetchEnvironmentsStatus(true); // Background refresh without loading indicator
    }, 30000); // 30 seconds

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [selectedProject?.id, selectedProject?.environments]); // Only depend on id and environments array

  const handleOpenConsole = () => {
    if (onOpenConsole) {
      onOpenConsole();
    } else if (selectedProject?.consoleUrl) {
      window.open(selectedProject.consoleUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDeleteProject = async () => {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await miaPlatformDataService.deleteProject(selectedProject.id);
      if (onProjectDeleted) {
        onProjectDeleted(selectedProject.id);
      }
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="dashboard-content">
      {/* Project Header with Banner Preview */}
      <div 
        className="dashboard-project-header"
        style={{
          backgroundImage: selectedResource?.data?.preview 
            ? `url(${selectedResource.data.preview})`
            : selectedProject?.thumbnail 
              ? `url(${selectedProject.thumbnail})`
              : undefined,
          backgroundSize: 'cover',
          backgroundPosition: '75% center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="header-overlay">
          <div className="project-title-section">
            <h1>{selectedProject?.name || 'Project Name'}</h1>
            <p className="project-description">{selectedProject?.description || 'No description available'}</p>
          </div>
          <button 
            onClick={handleOpenConsole}
            className="console-link-button fdark"
            type="button"
          >
            <Icon name="link" size={16} />
            Open in Console
          </button>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Left Column */}
        <div className="dashboard-column">
          {/* Technologies */}
          <div className="dashboard-card">
            <h3><Icon name="code" size={18} /> Technologies</h3>
            <div className="tech-tags">
              {configuration?.services ? (
                Array.from(new Set(
                  Object.values(configuration.services)
                    .map(s => s.dockerImage?.split(':')[0]?.split('/').pop())
                    .filter(Boolean)
                )).slice(0, 10).map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))
              ) : (
                <div className="empty-state">No technologies detected</div>
              )}
            </div>
          </div>

          {/* DORA Metrics */}
          <div className="dashboard-card dora-metrics">
            <h3><Icon name="chart" size={18} /> DORA Metrics</h3>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-label">Deployment Frequency</div>
                <div className="metric-value">--</div>
                <div className="metric-trend">Coming soon</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Lead Time</div>
                <div className="metric-value">--</div>
                <div className="metric-trend">Coming soon</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Change Failure Rate</div>
                <div className="metric-value">--</div>
                <div className="metric-trend">Coming soon</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Recovery Time</div>
                <div className="metric-value">--</div>
                <div className="metric-trend">Coming soon</div>
              </div>
            </div>
          </div>

          {/* Team & People */}
          <div className="dashboard-card">
            <h3><Icon name="users" size={18} /> Team</h3>
            <div className="team-list">
              {selectedProject?.team?.length > 0 ? (
                selectedProject.team.map((member, idx) => (
                  <div key={idx} className="team-member">
                    <div className="member-avatar">{member.name?.[0] || '?'}</div>
                    <div className="member-info">
                      <div className="member-name">{member.name || 'Unknown'}</div>
                      <div className="member-role">{member.role || 'Member'}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">No team members</div>
              )}
            </div>
          </div>

          {/* Next Actions */}
          <div className="dashboard-card next-actions">
            <h3><Icon name="check" size={18} /> Next Actions</h3>
            <div className="actions-list">
              <div className="action-item">
                <Icon name="code" size={14} />
                <span>Review pending code changes</span>
              </div>
              <div className="action-item">
                <Icon name="rocket" size={14} />
                <span>Deploy to staging environment</span>
              </div>
              <div className="action-item">
                <Icon name="file" size={14} />
                <span>Update documentation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="dashboard-column">
          {/* Environments */}
          <div className="dashboard-card">
            <h3>
              <Icon name="server" size={18} /> Environments
              <button 
                className="refresh-env-btn"
                onClick={() => fetchEnvironmentsStatus(true)}
                disabled={loadingStatus}
                title="Refresh environment status"
                type="button"
              >
                <Icon name="refresh" size={14} />
              </button>
            </h3>
            <div className="env-list">
              {loadingStatus ? (
                <div className="empty-state">Loading status...</div>
              ) : selectedProject?.environments?.length > 0 ? (
                selectedProject.environments.map((env, idx) => {
                  const envId = env.envId || env;
                  const status = environmentsStatus[envId];
                  const statusClass = status?.status || 'unknown';
                  
                  return (
                    <div key={idx} className="env-item">
                      <span className={`env-status ${statusClass}`} title={status?.error || statusClass}></span>
                      <div className="env-info">
                        <span className="env-name">{env.label || envId}</span>
                        {status && status.totalPods > 0 && (
                          <span className="env-pods-count">
                            {status.runningPods}/{status.totalPods} pods running
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">No environments configured</div>
              )}
            </div>
          </div>

          {/* Resources Grid - Services, Endpoints, Collections */}
          <div className="dashboard-resources-grid">
            {/* Services */}
            <div className="dashboard-card">
              <h3><Icon name="box" size={18} /> Services</h3>
              <div className="resource-count">
                {configuration?.services ? Object.keys(configuration.services).length : 0} services
              </div>
              <div className="resource-list">
                {configuration?.services && Object.entries(configuration.services).slice(0, 5).map(([key, service]) => (
                  <div 
                    key={key} 
                    className="resource-item" 
                    onClick={() => onResourceSelect && onResourceSelect({ type: 'service', name: key, data: service })}
                    role="button"
                    tabIndex={0}
                  >
                    <Icon name="box" size={14} />
                    <span>{service.name || key}</span>
                  </div>
                ))}
                {configuration?.services && Object.keys(configuration.services).length > 5 && (
                  <div className="resource-more">+{Object.keys(configuration.services).length - 5} more</div>
                )}
              </div>
            </div>

            {/* Endpoints */}
            <div className="dashboard-card">
              <h3><Icon name="link" size={18} /> Endpoints</h3>
              <div className="resource-count">
                {configuration?.endpoints ? Object.keys(configuration.endpoints).length : 0} endpoints
              </div>
              <div className="resource-list">
                {configuration?.endpoints && Object.entries(configuration.endpoints).slice(0, 5).map(([key, endpoint]) => (
                  <div key={key} className="resource-item">
                    <Icon name="link" size={14} />
                    <span>{endpoint.basePath || key}</span>
                  </div>
                ))}
                {configuration?.endpoints && Object.keys(configuration.endpoints).length > 5 && (
                  <div className="resource-more">+{Object.keys(configuration.endpoints).length - 5} more</div>
                )}
              </div>
            </div>

            {/* Collections */}
            {configuration?.collections && Object.keys(configuration.collections).length > 0 && (
              <div className="dashboard-card">
                <h3><Icon name="database" size={18} /> Collections</h3>
                <div className="resource-count">
                  {Object.keys(configuration.collections).length} collections
                </div>
                <div className="resource-list">
                  {Object.entries(configuration.collections).slice(0, 5).map(([key, collection]) => (
                    <div key={key} className="resource-item">
                      <Icon name="database" size={14} />
                      <span>{collection.name || key}</span>
                    </div>
                  ))}
                  {Object.keys(configuration.collections).length > 5 && (
                    <div className="resource-more">+{Object.keys(configuration.collections).length - 5} more</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Section */}
      <div className="dashboard-advanced-section">
        <details className="dashboard-advanced-details">
          <summary className="dashboard-advanced-summary">
            <Icon name="settings" size={16} />
            Advanced
          </summary>
          <div className="dashboard-advanced-content">
            <div className="dashboard-advanced-item">
              <div className="dashboard-advanced-item-info">
                <h4>Delete Project</h4>
                <p>Permanently delete this project and all its resources. This action cannot be undone.</p>
              </div>
              <button
                className="dashboard-delete-btn"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting || !selectedProject?.id}
                type="button"
              >
                {isDeleting ? 'Deleting…' : 'Delete Project'}
              </button>
            </div>
            {deleteError && (
              <div className="dashboard-delete-error">{deleteError}</div>
            )}
          </div>
        </details>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Project"
        message={`Are you sure you want to delete "${selectedProject?.name || 'this project'}"? This action cannot be undone.`}
        onConfirm={handleDeleteProject}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

DashboardView.propTypes = {
  selectedProject: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    thumbnail: PropTypes.string,
    consoleUrl: PropTypes.string,
    team: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      role: PropTypes.string
    })),
    environments: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        envId: PropTypes.string
      })
    ]))
  }),
  selectedResource: PropTypes.shape({
    data: PropTypes.shape({
      preview: PropTypes.string
    })
  }),
  configuration: PropTypes.shape({
    services: PropTypes.object,
    endpoints: PropTypes.object,
    collections: PropTypes.object
  }),
  onResourceSelect: PropTypes.func,
  onOpenConsole: PropTypes.func,
  onProjectDeleted: PropTypes.func
};

export default DashboardView;
